<template>
  <v-card
    class="pa-5 ml-10 mr-10 mt-5"
    variant="outlined"
    data-cy="promo-box">
    <v-card-title>{{ $t("views.licence.promo.title") }}</v-card-title>
    <v-card-text>
      <!-- what is active on the licence: type, code, status and how long it is valid -->
      <div
        v-if="customerTrial"
        class="d-flex align-center mb-2"
        data-cy="promo-customer-trial">
        <v-chip
          size="small"
          class="mr-2">
          {{ $t("views.licence.promo.types.customerTrial") }}
        </v-chip>
        <span data-cy="promo-customer-trial-end">
          {{
            $t("views.licence.promo.customerTrialLine", {
              date: new Date(customerTrial.end * 1000).toLocaleDateString()
            })
          }}
        </span>
        <v-chip
          v-if="customerTrial.autoEnd || customerTrial.continued"
          size="small"
          class="ml-2"
          :color="customerTrial.continued ? 'success' : 'info'"
          data-cy="promo-customer-trial-status">
          {{
            $t(
              "views.licence.promo.trialStatus." +
                (customerTrial.continued ? "continued" : "autoEnd")
            )
          }}
        </v-chip>
      </div>

      <div
        v-if="activeDiscount"
        class="d-flex align-center mb-2">
        <v-chip
          size="small"
          class="mr-2">
          {{ $t("views.licence.promo.types.discount") }}
        </v-chip>
        <v-chip
          color="success"
          data-cy="promo-active-discount">
          {{
            $t("views.licence.promo.active", {
              description: promo.describe(activeDiscount),
              code: activeDiscount.code,
              interpolation: { escapeValue: false }
            })
          }}
        </v-chip>
        <span
          class="ml-2"
          data-cy="promo-active-discount-validity">
          {{ discountValidity($t, activeDiscount) }}
        </span>
      </div>

      <!-- module trials of the licence -->
      <div
        v-for="trial in trials"
        :key="trial.module"
        class="d-flex align-center mb-2"
        :data-cy="'trial-' + trial.module">
        <v-chip
          size="small"
          class="mr-2">
          {{ $t("views.licence.promo.types.moduleTrial") }}
        </v-chip>
        <span class="mr-2">{{ promo.moduleName(trial.module) }}</span>
        <v-chip
          size="small"
          color="info"
          class="mr-2">
          {{
            $t("views.licence.promo.trialUntil", {
              date: new Date(trial.end_date).toLocaleDateString(),
              count: trial.quantity,
              interpolation: { escapeValue: false }
            })
          }}
        </v-chip>
        <v-btn
          v-if="showBook && (booked?.[trial.module] ?? 0) < trial.quantity"
          size="small"
          data-cy="trial-book"
          @click="emit('book', trial.module, trial.quantity)">
          {{ $t("views.licence.promo.bookPermanently") }}
        </v-btn>
      </div>

      <v-row
        align="center"
        dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="promo.promoCode.value"
            data-cy="promo-input"
            hide-details="auto"
            density="compact"
            :label="$t('views.licence.promo.label')"
            :disabled="!!promo.appliedCode.value"
            :error-messages="promo.promoError.value ?? undefined"
            @keyup.enter="promo.applyPromo()" />
        </v-col>
        <v-col cols="auto">
          <v-btn
            v-if="!promo.appliedCode.value"
            data-cy="promo-apply"
            :loading="promo.applying.value"
            :disabled="!promo.promoCode.value.trim()"
            @click="promo.applyPromo()">
            {{ $t("views.licence.promo.apply") }}
          </v-btn>
          <v-btn
            v-else
            data-cy="promo-remove"
            @click="promo.removePromo()">
            {{ $t("views.licence.promo.remove") }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- status of the entered code -->
      <v-chip
        v-if="promo.promoInfo.value && promo.appliedCode.value"
        class="mt-2"
        color="success"
        closable
        data-cy="promo-chip"
        @click:close="promo.removePromo()">
        {{ promo.describe(promo.promoInfo.value) }}
      </v-chip>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { discountValidity, usePromo } from "./moduleComposable";

const props = defineProps<{
  promo: ReturnType<typeof usePromo>;
  trials?: { module: string; quantity: number; end_date: string }[];
  activeDiscount?: any;
  // running customer trial of the licence (private licence page only)
  customerTrial?: { end: number; autoEnd: boolean; continued: boolean } | null;
  // book button only where the parent handles @book
  showBook?: boolean;
  // trial slots already booked per module
  booked?: Record<string, number>;
}>();
const emit = defineEmits<{
  (e: "book", module: string, quantity: number): void;
}>();
</script>
